import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import Navbar from '../components/common/Navbar';
import Sidebar from '../components/common/Sidebar';
import useIdleTimeout from '../hooks/useIdleTimeout';
import { clearSession } from '../utils/session';
import './DashboardLayout.css';

const IDLE_MINUTES   = 5;
const WARNING_SECS   = 30;
const IDLE_MS        = IDLE_MINUTES * 60 * 1000;
const WARNING_MS     = WARNING_SECS * 1000;

const DashboardLayout = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen]   = useState(false);
  const [showWarning, setShowWarning]   = useState(false);
  const [countdown, setCountdown]       = useState(WARNING_SECS);
  const countdownRef                    = useRef(null);

  // ── Logout helper ──────────────────────────────────────────────
  const doLogout = useCallback(() => {
    clearInterval(countdownRef.current);
    setShowWarning(false);
    clearSession();
    navigate('/login', { replace: true });
  }, [navigate]);

  // ── Warning: start countdown ticker ───────────────────────────
  const handleWarning = useCallback(() => {
    setCountdown(WARNING_SECS);
    setShowWarning(true);
    clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  // ── User stayed active — dismiss warning ──────────────────────
  const handleActivity = useCallback(() => {
    if (showWarning) {
      clearInterval(countdownRef.current);
      setShowWarning(false);
    }
  }, [showWarning]);

  // ── Mount idle hook ────────────────────────────────────────────
  const { resetTimers } = useIdleTimeout({
    idleMs:     IDLE_MS,
    warningMs:  WARNING_MS,
    onIdle:     doLogout,
    onWarning:  handleWarning,
    onActivity: handleActivity,
  });

  // ── "Stay Logged In" button ────────────────────────────────────
  const handleStayLoggedIn = () => {
    clearInterval(countdownRef.current);
    setShowWarning(false);
    resetTimers();
  };

  // Cleanup on unmount
  useEffect(() => () => clearInterval(countdownRef.current), []);

  return (
    <div className="dashboard-layout" data-testid="dashboard-layout">
      <Navbar isAuthenticated={true} onMenuToggle={() => setSidebarOpen(!sidebarOpen)} />
      <div className="dashboard-container">
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <main className="dashboard-main">
          <div className="dashboard-content">
            <Outlet />
          </div>
        </main>
      </div>

      {/* ═══════════════════════════════════════════════════
          SESSION EXPIRY WARNING MODAL
      ═══════════════════════════════════════════════════ */}
      {showWarning && (
        <div className="session-overlay">
          <div className="session-modal">
            {/* Animated ring countdown */}
            <div className="session-ring">
              <svg viewBox="0 0 60 60" className="session-ring-svg">
                <circle cx="30" cy="30" r="26" className="session-ring-track" />
                <circle
                  cx="30"
                  cy="30"
                  r="26"
                  className="session-ring-fill"
                  style={{
                    strokeDashoffset: `${163.4 - (163.4 * countdown) / WARNING_SECS}`,
                  }}
                />
              </svg>
              <span className="session-ring-number">{countdown}</span>
            </div>

            <h4 className="session-title">Session Expiring Soon</h4>
            <p className="session-desc">
              You've been inactive for <strong>{IDLE_MINUTES} minutes</strong>.<br />
              You'll be logged out automatically in{' '}
              <strong style={{ color: countdown <= 10 ? '#ff4d4d' : 'var(--accent-color)' }}>
                {countdown}s
              </strong>
              .
            </p>

            <div className="session-actions">
              <button className="btn btn-gradient session-btn-stay" onClick={handleStayLoggedIn}>
                <i className="bi bi-arrow-clockwise me-2"></i>Stay Logged In
              </button>
              <button className="btn session-btn-logout" onClick={doLogout}>
                <i className="bi bi-box-arrow-right me-2"></i>Logout Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardLayout;

